import type { QueryClient } from "@tanstack/react-query";
import { applicationQueryKey } from "@/lib/application-query";

type ApplicationCache = Record<string, unknown> & {
  stage?: string;
  lender_submissions?: {
    id: number;
    status?: string;
    approval_economics?: Record<string, unknown> | null;
  }[];
  funded_deal_economics?: Record<string, unknown> | null;
};

/** Merge partial updates into cached application; optional background refetch. */
export function patchApplicationCache(
  queryClient: QueryClient,
  applicationId: string,
  patch: (prev: ApplicationCache) => ApplicationCache,
  options?: { invalidateAfterMs?: number }
) {
  const key = applicationQueryKey(applicationId);
  queryClient.setQueryData<ApplicationCache>(key, (prev) => {
    if (!prev) return prev;
    return patch({ ...prev });
  });
  const delay = options?.invalidateAfterMs;
  if (delay != null && delay > 0) {
    window.setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: key });
    }, delay);
  }
}

export function mergeApprovalEconomicsInCache(
  queryClient: QueryClient,
  applicationId: string,
  submissionId: number,
  economics: Record<string, unknown>
) {
  patchApplicationCache(queryClient, applicationId, (prev) => {
    const subs = prev.lender_submissions ?? [];
    return {
      ...prev,
      lender_submissions: subs.map((s) =>
        s.id === submissionId
          ? {
              ...s,
              approval_economics: { ...(s.approval_economics ?? {}), ...economics },
            }
          : s
      ),
    };
  });
}

export function mergeFundedEconomicsInCache(
  queryClient: QueryClient,
  applicationId: string,
  economics: Record<string, unknown>
) {
  patchApplicationCache(queryClient, applicationId, (prev) => ({
    ...prev,
    funded_deal_economics: { ...(prev.funded_deal_economics ?? {}), ...economics },
  }));
}

export function mergeLenderSubmissionInCache(
  queryClient: QueryClient,
  applicationId: string,
  submissionId: number,
  patch: Partial<{ status: string; notes: string }>
) {
  patchApplicationCache(queryClient, applicationId, (prev) => {
    const subs = prev.lender_submissions ?? [];
    return {
      ...prev,
      lender_submissions: subs.map((s) => (s.id === submissionId ? { ...s, ...patch } : s)),
    };
  });
}
